import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { ILimitResponse, limitsQueryConfig } from "../../../../../api";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RoutesMap } from "../../../../../routes.tsx";

/**
 * Polls limit status and navigates when status changes from WAITING_FOR_CONFIRMATION
 */
const LIMIT_POLL_INTERVAL_MS = 10_000;

// Poll until we reach a final/redirectable state.
// This is intentionally a denylist so new intermediate statuses won't break the flow.
const LIMIT_STATUSES_STOP_POLLING = new Set<string>([
  // terminal
  "ACTIVE",
  "BLOCKED",
  // current product behavior: treat CONFIRMED as "done"
  "CONFIRMED",
  // redirect targets
  "WAITING_FOR_CARD_ISSUE",
  "DECLINED_SPR",
  "DECLINED_TIMEOUT",
  "DECLINED",
]);

function shouldPollOnLimitSuccess(status: string | undefined) {
  if (!status) return false;
  return !LIMIT_STATUSES_STOP_POLLING.has(status);
}

type TLimitSuccessView = "screen";

export function useLimitSuccessFlow() {
  const navigate = useNavigate();
  const lastHandledRef = useRef<string | null>(null);

  const limits = useQuery({
    ...limitsQueryConfig(),
    refetchInterval: (query) => {
      const { data } = query.state;
      const firstLimit = data?.[0];
      return shouldPollOnLimitSuccess(firstLimit?.status) ? LIMIT_POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: true,
  }) as UseQueryResult<ILimitResponse[]>;

  const firstLimit = limits.data?.[0];
  const limitStatus = firstLimit?.status;

  const approvedAmount = firstLimit?.totalAmount ?? 0;
  const isAmountReady = limits.isSuccess && !!firstLimit;

  const view: TLimitSuccessView = "screen";

  useEffect(() => {
    if (!limits.isSuccess) {
      return;
    }

    const firstLimit = limits.data[0] as ILimitResponse | undefined;
    if (!firstLimit) {
      return;
    }

    const handledKey = `${firstLimit.id}:${firstLimit.status}`;
    if (lastHandledRef.current === handledKey) {
      return;
    }

    lastHandledRef.current = handledKey;

    if (shouldPollOnLimitSuccess(firstLimit.status)) {
      return;
    }

    // Status changed, navigate based on new status
    switch (firstLimit.status) {
      case "CONFIRMED":
      case "BLOCKED":
      case "ACTIVE": {
        navigate(RoutesMap.index);
        return;
      }
      case "WAITING_FOR_CARD_ISSUE": {
        navigate(RoutesMap.login.loading);
        return;
      }
      case "DECLINED_SPR":
      case "DECLINED_TIMEOUT":
      case "DECLINED": {
        navigate(RoutesMap.login.limitReject);
        return;
      }
      default: {
        // Unknown status: stay here and keep polling via shouldPollOnLimitSuccess().
        return;
      }
    }
  }, [limits.isSuccess, limits.data, navigate]);

  const debug = useMemo(() => ({
    limitId: firstLimit?.id,
    limitStatus,
    isPolling: shouldPollOnLimitSuccess(limitStatus),
  }), [firstLimit?.id, limitStatus]);

  return {
    limits,
    firstLimit,
    limitStatus,
    view,
    approvedAmount,
    isAmountReady,
    debug,
  };
}
